import { Card, Colors, Heading, Icon, Text, type IconName } from '@amelio/ui-library';
import { useMemo } from 'react';

import {
    ManagerAssessmentSkillSelectorOption,
    type ManagerAssessmentSkillSelectorOptionValue,
} from '@/constants/review/ManagerAssessmentSkillSelectorOption';
import { useAmelioTranslations } from '@/hooks';

interface ManagerSkillSelectorSectionProps {
    value?: ManagerAssessmentSkillSelectorOptionValue;
    onChange: (value: ManagerAssessmentSkillSelectorOptionValue) => void;
    isDisabled?: boolean;
}

interface SkillSelectorOptionCard {
    value: ManagerAssessmentSkillSelectorOptionValue;
    label: string;
    description: string;
    icon: IconName;
}

export const ManagerSkillSelectorSection = ({
    value,
    onChange,
    isDisabled = false,
}: ManagerSkillSelectorSectionProps) => {
    const { t } = useAmelioTranslations();

    const options: SkillSelectorOptionCard[] = useMemo(
        () => [
            {
                value: ManagerAssessmentSkillSelectorOption.ALL_SKILLS,
                label: t(
                    'templateManagement.template.managerSkillSelector.options.allSkills.label',
                ),
                description: t(
                    'templateManagement.template.managerSkillSelector.options.allSkills.description',
                ),
                icon: 'security-shield-tick',
            },
            {
                value: ManagerAssessmentSkillSelectorOption.WEAKEST_SKILLS,
                label: t(
                    'templateManagement.template.managerSkillSelector.options.weakestSkills.label',
                ),
                description: t(
                    'templateManagement.template.managerSkillSelector.options.weakestSkills.description',
                ),
                icon: 'design-layer',
            },
            {
                value: ManagerAssessmentSkillSelectorOption.OLDER_THAN_SIX_MONTHS,
                label: t(
                    'templateManagement.template.managerSkillSelector.options.olderThanSixMonths.label',
                ),
                description: t(
                    'templateManagement.template.managerSkillSelector.options.olderThanSixMonths.description',
                ),
                icon: 'time-clock',
            },
            {
                value: ManagerAssessmentSkillSelectorOption.DO_NOT_ASSESS_SKILLS,
                label: t(
                    'templateManagement.template.managerSkillSelector.options.doNotAssessSkills.label',
                ),
                description: t(
                    'templateManagement.template.managerSkillSelector.options.doNotAssessSkills.description',
                ),
                icon: 'essential-close-circle',
            },
        ],
        [t],
    );

    return (
        <Card
            className={'mt-2'}
            padding={'xl'}
            borderRadius={'2xl'}
            dataTestId={'manager-skill-selector-section'}
        >
            <div className={'flex items-center justify-center gap-4 sm:!justify-start'}>
                <div
                    className={
                        'bg-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-full'
                    }
                >
                    <Text
                        weight={'semibold'}
                        color={Colors.WHITE}
                        className={'text-lg'}
                    >
                        2
                    </Text>
                </div>
                <Heading
                    weight={'medium'}
                    className={'text-left text-2xl'}
                >
                    {t('templateManagement.template.managerSkillSelector.title')}
                </Heading>
            </div>

            {/* <Text className={'mt-2'}>
                {t('templateManagement.template.managerSkillSelector.description')}
            </Text> */}

            <div
                className={'mt-8 grid grid-cols-1 gap-6 md:grid-cols-2'}
                data-testid={'manager-skill-selector-radio-group'}
            >
                {options.map((option) => {
                    const isSelected = value === option.value;

                    return (
                        <button
                            key={option.value}
                            type={'button'}
                            data-testid={`manager-skill-selector-option-${option.value}`}
                            onClick={() => onChange(option.value)}
                            disabled={isDisabled}
                            className={`focus-visible:ring-primary-800 relative rounded-2xl border-2 p-8 text-left focus:outline-none focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${isSelected ? 'border-primary bg-[rgba(13,211,188,0.05)]' : 'border-base-100 hover:border-base-200 bg-white'}`}
                        >
                            {isSelected && (
                                <div
                                    className={
                                        'bg-primary absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full'
                                    }
                                >
                                    <Icon
                                        name={'essential-checkmark'}
                                        size={'sm'}
                                        color={Colors.WHITE}
                                    />
                                </div>
                            )}

                            <div
                                className={
                                    'flex flex-col items-center gap-4 sm:!items-start md:flex-row'
                                }
                            >
                                <div
                                    className={`mt-0.5 flex size-14 shrink-0 items-center justify-center rounded-lg ${isSelected ? 'bg-white/70' : 'bg-base-50'}`}
                                >
                                    <Icon
                                        name={option.icon}
                                        color={Colors.SECONDARY}
                                        strokeWidth={2}
                                        size={'lg'}
                                    />
                                </div>

                                <div className={'pt-0.5'}>
                                    <Text
                                        weight={'semibold'}
                                        className={'text-center text-xl sm:!text-left'}
                                    >
                                        {option.label}
                                    </Text>

                                    <Text
                                        size={'small'}
                                        color={Colors.BASE_400}
                                        className={'mt-1 text-center sm:!text-left'}
                                    >
                                        {option.description}
                                    </Text>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>
        </Card>
    );
};
